import type {
  AgentDetail,
  AgentSummary,
  AppConfig,
  CreateAgentRequest,
  EvaluatePromptResponse,
  FeedbackRequest,
  FuturesBalanceSummary,
  FuturesPosition,
  FuturesProductsResponse,
  LeaderboardResponse,
  ModelInfo,
  Portfolio,
  PortfolioBreakdown,
  PreviewAgentRequest,
  PreviewAgentResponse,
  User,
} from './types';

// All API calls use relative paths (same-origin).
// Locally, Next.js proxies /api/* to the Go backend on :8080.
// In deployed environments, the entry gateway handles this routing.
const API_BASE = '';

async function fetchJSON<T>(path: string, options?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {};
  // Only set Content-Type when sending a body (POST/PUT/PATCH) to avoid
  // unnecessary CORS preflight on simple GET/DELETE requests.
  if (options?.body) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers,
    ...options,
  });
  if (!res.ok) {
    const raw = await res.text();
    let errorMessage = raw;
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed.error === 'string') {
        errorMessage = parsed.error;
      }
    } catch {
      // Response wasn't JSON; use the raw text as-is.
    }
    // If the backend returns 401, the session has expired or is invalid.
    // Dispatch an event so the UserProvider can clear auth state and redirect.
    if (res.status === 401) {
      window.dispatchEvent(new Event('auth:expired'));
    }
    if (res.status === 403 && errorMessage.includes('terms updated')) {
      window.dispatchEvent(new Event('tos:reaccept'));
    }
    const message = errorMessage.trim() || `API error (${res.status})`;
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

type AsyncResult<T> =
  | { status: 'pending' }
  | { status: 'done'; data: T }
  | { status: 'error'; error: string };

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 60; // 2s * 60 = 2 min max

async function pollForResult<T>(requestId: string): Promise<T> {
  for (let i = 0; i < POLL_MAX_ATTEMPTS; i++) {
    await new Promise<void>((resolve) => {
      setTimeout(resolve, POLL_INTERVAL_MS);
    });
    const result = await fetchJSON<AsyncResult<T>>(`/api/me/agents/prompt-result/${requestId}`);
    if (result.status === 'done') return result.data;
    if (result.status === 'error') throw new Error(result.error);
  }
  throw new Error('Request timed out');
}

export const api = {
  // App-level config (guardrails, limits). Public endpoint, no auth required.
  getConfig: async () => fetchJSON<AppConfig>('/api/config'),

  // Auth
  getMe: async () => fetchJSON<User>('/api/me'),
  completeOnboarding: async () =>
    fetchJSON<{ onboarding_completed: boolean }>('/api/me/onboarding', {
      method: 'POST',
    }),

  // Portfolios (OAuth users)
  getMyPortfolios: async () => fetchJSON<Portfolio[]>('/api/me/portfolios'),
  getPortfolioBreakdown: async (portfolioId: string) =>
    fetchJSON<PortfolioBreakdown>(`/api/me/portfolios/${portfolioId}`),

  // CFM futures (derivatives-eligible users only; 403 otherwise).
  // Returns an envelope with `onboarded` so the UI can render a guidance
  // banner when the user is eligible but hasn't completed Coinbase's CFM
  // onboarding yet.
  getFuturesProducts: async () => fetchJSON<FuturesProductsResponse>('/api/me/futures/products'),
  getFuturesBalanceSummary: async () =>
    fetchJSON<FuturesBalanceSummary>('/api/me/futures/balance-summary'),
  getFuturesPositions: async () => fetchJSON<FuturesPosition[]>('/api/me/futures/positions'),

  // Models
  getModels: async () => fetchJSON<ModelInfo[]>('/api/models'),

  // Agents (user-scoped, via /api/me)
  getMyAgents: async () => fetchJSON<AgentSummary[]>('/api/me/agents'),

  getMyAgent: async (agentId: string) => fetchJSON<AgentDetail>(`/api/me/agents/${agentId}`),

  createAgent: async (req: CreateAgentRequest) =>
    fetchJSON<AgentSummary>('/api/me/agents', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  updateAgentStrategy: async (agentId: string, strategy: string) =>
    fetchJSON<{ status: string }>(`/api/me/agents/${agentId}`, {
      method: 'PATCH',
      body: JSON.stringify({ strategy }),
    }),

  stopAgent: async (agentId: string) =>
    fetchJSON<{ status: AgentDetail['status'] }>(`/api/me/agents/${agentId}`, {
      method: 'DELETE',
    }),

  pauseAgent: async (agentId: string) =>
    fetchJSON<{ status: AgentDetail['status'] }>(`/api/me/agents/${agentId}/pause`, {
      method: 'POST',
    }),

  resumeAgent: async (agentId: string) =>
    fetchJSON<{ status: AgentDetail['status'] }>(`/api/me/agents/${agentId}/resume`, {
      method: 'POST',
    }),

  // Strategy evaluation (async: POST starts the job, then poll for result)
  evaluatePrompt: async (strategy: string) => {
    const { request_id } = await fetchJSON<{ request_id: string }>(
      '/api/me/agents/improve-prompt',
      { method: 'POST', body: JSON.stringify({ strategy }) },
    );
    return pollForResult<EvaluatePromptResponse>(request_id);
  },

  previewAgent: async (req: PreviewAgentRequest) => {
    const { request_id } = await fetchJSON<{ request_id: string }>('/api/me/agents/preview', {
      method: 'POST',
      body: JSON.stringify(req),
    });
    return pollForResult<PreviewAgentResponse>(request_id);
  },

  // Agents (public read-only, portfolio info stripped)
  getAgentPublic: async (agentId: string) => fetchJSON<AgentDetail>(`/api/agents/${agentId}`),

  // Feedback
  submitFeedback: async (req: FeedbackRequest) =>
    fetchJSON<{ status: string }>('/api/me/feedback', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  // Leaderboard
  getLeaderboard: async (params?: {
    pageSize?: number;
    pageToken?: string;
    sortBy?: string;
    statusFilter?: string;
    // Asset-type filter: 'all' | 'spot' | 'futures' | 'mixed'. Backend
    // ignores unknown values and returns the unfiltered set.
    assetType?: string;
    // Time-period filter: 'all' | 'today' | 'week' | 'month'. Omitted or
    // 'all' requests the legacy lifetime view; other values return period-
    // scoped pnl / return_pct / trade_count per row.
    timePeriod?: string;
  }) => {
    const qs = new URLSearchParams();
    if (params?.pageSize) qs.set('page_size', String(params.pageSize));
    if (params?.pageToken) qs.set('page_token', params.pageToken);
    if (params?.sortBy) qs.set('sort_by', params.sortBy);
    if (params?.statusFilter) qs.set('status_filter', params.statusFilter);
    if (params?.assetType && params.assetType !== 'all') qs.set('asset_type', params.assetType);
    if (params?.timePeriod && params.timePeriod !== 'all') qs.set('time_period', params.timePeriod);
    const query = qs.toString();
    const path = query ? `/api/leaderboard?${query}` : '/api/leaderboard';
    return fetchJSON<LeaderboardResponse>(path);
  },
};
